import React, { useState } from 'react';
import { Stack, Box, Typography, Button, Avatar, Chip, Paper, Tooltip, ClickAwayListener, CircularProgress } from 'omaha-design-system';
import { Plus as AddIcon, Minus as RemoveIcon, Lock as LockIcon, PencilSimple as EditIcon, CaretDown } from '@phosphor-icons/react';
import { SportsbookCard } from './ExpandedOddsPanel';
import { SportsbookLogo } from './SportsbookLogo';
import { BetSlipItem, PlayerPropSportsbook } from '../data/types';
import { getInjuryBadge } from '../data/transformPropsData';
import { STAT_LABELS } from '../data/statLabels';
import { isActualMobileDevice, getPlatformDeepLink } from '../data/deeplinkHelper';
import { useAllBooksSportsbooks } from '../hooks/useAllBooksSportsbooks';
import BattingOrderBadge from './BattingOrderBadge';
import { leagueHasMinutes } from '../data/leagueConfig';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store/reducer';

interface InjuredTeammate {
  name: string;
  status?: string;
  notes?: string;
}

interface MobileBetCardProps {
  bet: {
    playerId: string;
    playerName: string;
    avatar: string;
    position?: string;
    matchup: string;
    startDate?: string;
    stat: string;
    originalStat: string;
    projection: number;
    bookLine: number;
    side: 'Over' | 'Under';
    odds: string;
    book: string;
    bookIcon?: string;
    bookWin: number;
    modelWin: number;
    marketWin: number;
    evPct: number;
    edge: number;
    minutes?: string;
    /** Custom minutes if player has customizations */
    customMinutes?: number;
    /** Whether this player has custom projections applied */
    hasCustomizations?: boolean;
    deepLinkUrl?: string;
    sportsbooks?: PlayerPropSportsbook[];
    injuryStatus?: string;
    injuryNotes?: string;
    injuredTeammates?: InjuredTeammate[];
    battingOrder?: number;
    confirmedLineup?: boolean | null;
    hand?: string;
  };
  isInBetSlip?: boolean;
  isLocked?: boolean;
  isExpanded?: boolean;
  onAddToBetSlip: (bet: BetSlipItem) => void;
  onRemoveFromBetSlip?: () => void;
  onOtherBooksClick?: () => void;
  onToggleExpand?: () => void;
}

export const MobileBetCard: React.FC<MobileBetCardProps> = ({ bet, isInBetSlip, isLocked, isExpanded, onAddToBetSlip, onRemoveFromBetSlip, onOtherBooksClick, onToggleExpand }) => {
  const evPctValue = bet.evPct ?? 0;
  const isPositiveEv = evPctValue >= 0;
  const otherBooksCount = (bet.sportsbooks?.length ?? 1) - 1;
  // Leagues without projected minutes (MLB/NFL) come through as "0"; never render that.
  const hasProjectedMinutes = !!bet.minutes && parseFloat(bet.minutes) > 0;
  const league = useSelector((state: IRootState) => state.stokasticProps.filters.league);

  // Fetch ALL sportsbooks (ignoring user's filter) for the expanded panel
  const { getSportsbooks, isLoading: allBooksLoading } = useAllBooksSportsbooks();
  const allSportsbooks = React.useMemo(
    () => getSportsbooks(bet.playerName, bet.originalStat, bet.side, bet.sportsbooks || []),
    [getSportsbooks, bet.playerName, bet.originalStat, bet.side, bet.sportsbooks],
  );

  // Tooltip open states
  const [evTooltipOpen, setEvTooltipOpen] = useState(false);
  const [stokTooltipOpen, setStokTooltipOpen] = useState(false);
  const [injuryTooltipOpen, setInjuryTooltipOpen] = useState(false);
  const [tmTooltipOpen, setTmTooltipOpen] = useState(false);

  const handleAdd = () => {
    onAddToBetSlip({
      id: `${bet.playerId}-${bet.stat}-${bet.book}-${bet.side}`,
      playerName: bet.playerName,
      avatar: bet.avatar,
      stat: bet.stat,
      projection: bet.projection.toFixed(1),
      bookLine: bet.bookLine.toFixed(1),
      edge: bet.edge.toFixed(1),
      bookWin: bet.bookWin,
      modelWin: bet.modelWin,
      evPct: bet.evPct,
      book: bet.book,
      bookLogo: bet.bookIcon,
      odds: bet.odds,
      side: bet.side,
      deepLinkUrl: bet.deepLinkUrl,
    });
  };

  const handleBetSlipClick = () => {
    if (isInBetSlip && onRemoveFromBetSlip) {
      onRemoveFromBetSlip();
    } else {
      handleAdd();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
        backgroundColor: '#1E1F20',
        borderRadius: '12px',
        padding: { xs: '8px', sm: '16px' },
        border: '1px solid rgba(255, 255, 255, 0.08)',
      }}
    >
      {/* Row 1: Player Info + EV Chip */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Stack direction="row" alignItems="center" gap="16px">
          <Avatar
            src={bet.avatar}
            sx={{ width: 32, height: 32 }}
          >
            {bet.playerName.split(' ').map((n) => n[0]).join('')}
          </Avatar>
          <Stack>
            <Stack direction="row" alignItems="center" gap="8px">
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: '13.33px',
                  lineHeight: 1.5,
                  color: '#F2F2F3',
                }}
              >
                {bet.playerName}
              </Typography>
              {(() => {
                const injuryBadge = getInjuryBadge(bet.injuryStatus);
                return injuryBadge ? (
                  <ClickAwayListener onClickAway={() => setInjuryTooltipOpen(false)}>
                    <div>
                      <Tooltip
                        title={bet.injuryNotes || bet.injuryStatus || ''}
                        arrow
                        open={injuryTooltipOpen}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        onClose={() => setInjuryTooltipOpen(false)}
                      >
                        <Box
                          onClick={() => setInjuryTooltipOpen(!injuryTooltipOpen)}
                          sx={{
                            px: 0.75,
                            py: 0.125,
                            borderRadius: '4px',
                            backgroundColor: injuryBadge.bgColor,
                            fontSize: '10px',
                            fontWeight: 700,
                            color: injuryBadge.textColor,
                            lineHeight: 1.4,
                            cursor: 'pointer',
                          }}
                        >
                          {injuryBadge.label}
                        </Box>
                      </Tooltip>
                    </div>
                  </ClickAwayListener>
                ) : null;
              })()}
              {(() => {
                const injuredTeammates = bet.injuredTeammates?.filter(t => t.status) || [];
                if (injuredTeammates.length === 0) return null;
                const tooltipText = `Teammate${injuredTeammates.length > 1 ? 's' : ''} injured: ${injuredTeammates.map(t => `${t.name} (${t.status})`).join(', ')}`;
                return (
                  <ClickAwayListener onClickAway={() => setTmTooltipOpen(false)}>
                    <div>
                      <Tooltip
                        title={tooltipText}
                        arrow
                        open={tmTooltipOpen}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        onClose={() => setTmTooltipOpen(false)}
                      >
                        <Box
                          onClick={() => setTmTooltipOpen(!tmTooltipOpen)}
                          sx={{
                            px: 0.75,
                            py: 0.125,
                            borderRadius: '4px',
                            backgroundColor: 'rgba(245, 124, 0, 0.2)',
                            border: '1px solid rgba(245, 124, 0, 0.6)',
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#f57c00',
                            lineHeight: 1.4,
                            cursor: 'pointer',
                          }}
                        >
                          TM ({injuredTeammates.length})
                        </Box>
                      </Tooltip>
                    </div>
                  </ClickAwayListener>
                );
              })()}
              {bet.battingOrder !== undefined && (
                <BattingOrderBadge battingOrder={bet.battingOrder} confirmedLineup={bet.confirmedLineup} />
              )}
            </Stack>
            <Typography
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                lineHeight: 1.5,
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              {bet.position ? `${bet.position}${bet.hand ? ` (${bet.hand})` : ''} - ` : ''}{bet.matchup}{bet.startDate ? `  ${bet.startDate}` : ''}
            </Typography>
          </Stack>
        </Stack>

        {/* EV Chip */}
        <ClickAwayListener onClickAway={() => setEvTooltipOpen(false)}>
          <div>
            <Tooltip
              title="EV % = the amount you can expect to win or lose per bet"
              arrow
              open={evTooltipOpen}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              onClose={() => setEvTooltipOpen(false)}
            >
              {isLocked ? (
                <Chip
                  icon={<LockIcon size={16} color="rgba(255, 255, 255, 0.3)" />}
                  label="EV"
                  onClick={() => setEvTooltipOpen(!evTooltipOpen)}
                  sx={{
                    height: 32,
                    px: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: 1.125,
                      color: 'rgba(255, 255, 255, 0.3)',
                      padding: 0,
                    },
                  }}
                />
              ) : (
                <Chip
                  label={`${isPositiveEv ? '+' : ''}${evPctValue.toFixed(1)}% EV`}
                  onClick={() => setEvTooltipOpen(!evTooltipOpen)}
                  sx={{
                    height: 32,
                    px: '8px',
                    backgroundColor: isPositiveEv ? 'rgba(102, 187, 106, 0.08)' : 'rgba(239, 83, 80, 0.08)',
                    border: isPositiveEv ? '1px solid #66BB6A' : '1px solid #EF5350',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: 1.125,
                      color: isPositiveEv ? '#66BB6A' : '#EF5350',
                      padding: 0,
                    },
                  }}
                />
              )}
            </Tooltip>
          </div>
        </ClickAwayListener>
      </Stack>

      {/* Row 2: Minutes + Line Info */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Stack direction="row" alignItems="center" gap="8px">
          {leagueHasMinutes(league) && (hasProjectedMinutes || bet.customMinutes != null) && (
            bet.hasCustomizations ? (
              <Tooltip title="Minutes adjusted by user" arrow>
                <Stack direction="row" alignItems="center" gap="4px">
                  <EditIcon size={14} color="#FFA726" />
                  <Typography
                    sx={{
                      fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                      fontWeight: 600,
                      fontSize: '13.33px',
                      lineHeight: 1.5,
                      color: '#FFA726',
                    }}
                  >
                    {bet.customMinutes != null ? bet.customMinutes.toFixed(1) : bet.minutes} min
                  </Typography>
                </Stack>
              </Tooltip>
            ) : (
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: '13.33px',
                  lineHeight: 1.5,
                  color: '#F2F2F3',
                }}
              >
                {bet.minutes} min
              </Typography>
            )
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap="8px">
          <SportsbookLogo book={bet.book} bookIcon={bet.bookIcon} size={20} borderRadius="4px" />
          <Typography
            sx={{
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: '13.33px',
              lineHeight: 1.5,
              color: '#F2F2F3',
            }}
          >
            {STAT_LABELS[bet.stat] || bet.stat}
          </Typography>
          {isLocked ? (
            /* Teaser row: line and odds never reach the client */
            <LockIcon size={14} color="rgba(255, 255, 255, 0.3)" />
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 400,
                  fontSize: '13.33px',
                  lineHeight: 1.5,
                  color: '#F2F2F3',
                }}
              >
                {bet.side === 'Under' ? 'u' : 'o'}{bet.bookLine.toFixed(1)}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                  fontWeight: 400,
                  fontSize: '13.33px',
                  lineHeight: 1.5,
                  color: 'rgba(242, 242, 243, 0.7)',
                }}
              >
                {bet.odds}
              </Typography>
            </>
          )}
          {otherBooksCount > 0 && !isLocked && (
            <Typography
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                onOtherBooksClick?.();
              }}
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                lineHeight: 1.5,
                color: '#42A5F5',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              +{otherBooksCount} books
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Row 3: Stats Cards */}
      <Stack
        direction="row"
        gap="4px"
        sx={{ width: '100%' }}
      >
        {/* Projection Card */}
        <Paper
          sx={{
            flex: 1,
            backgroundColor: '#2A2B2C',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: '8px',
            px: '4px',
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 400,
              fontSize: '11.11px',
              lineHeight: 1.22,
              color: 'rgba(242, 242, 243, 0.7)',
            }}
          >
            Orig. Proj
          </Typography>
          {isLocked ? (
            <LockIcon size={14} color="rgba(255, 255, 255, 0.3)" />
          ) : (
            <Typography
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 600,
                fontSize: '13.33px',
                lineHeight: 1.5,
                color: '#F2F2F3',
              }}
            >
              {bet.projection.toFixed(2)}
            </Typography>
          )}
        </Paper>

        {/* True Odds Card */}
        <Paper
          sx={{
            flex: 1,
            backgroundColor: '#2A2B2C',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: '8px',
            px: '4px',
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 400,
              fontSize: '11.11px',
              lineHeight: 1.22,
              color: 'rgba(242, 242, 243, 0.7)',
            }}
          >
            Market Proj.
          </Typography>
          {isLocked ? (
            <LockIcon size={14} color="rgba(255, 255, 255, 0.3)" />
          ) : (
            <Typography
              sx={{
                fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                fontWeight: 600,
                fontSize: '13.33px',
                lineHeight: 1.5,
                color: '#F2F2F3',
              }}
            >
              {bet.marketWin && bet.marketWin > 0
                ? (bet.marketWin >= 0.5
                    ? `${Math.round(-100 * bet.marketWin / (1 - bet.marketWin))}`
                    : `+${Math.round(100 * (1 - bet.marketWin) / bet.marketWin)}`)
                : '-'}
            </Typography>
          )}
        </Paper>

        {/* Model Win % Card */}
        <ClickAwayListener onClickAway={() => setStokTooltipOpen(false)}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tooltip
              title="Model probability derived from your blend of projection and market true odds vs the line"
              arrow
              open={stokTooltipOpen}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              onClose={() => setStokTooltipOpen(false)}
            >
              <Paper
                onClick={() => setStokTooltipOpen(!stokTooltipOpen)}
                sx={{
                  flex: 1,
                  backgroundColor: '#2A2B2C',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: '8px',
                  px: '4px',
                  minWidth: 0,
                  cursor: 'pointer',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                    fontWeight: 400,
                    fontSize: '11.11px',
                    lineHeight: 1.22,
                    color: 'rgba(242, 242, 243, 0.7)',
                  }}
                >
                  Model Win %
                </Typography>
                {isLocked ? (
                  <LockIcon size={14} color="rgba(255, 255, 255, 0.3)" />
                ) : (
                  <Typography
                    sx={{
                      fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                      fontWeight: 600,
                      fontSize: '13.33px',
                      lineHeight: 1.5,
                      color: '#42A5F5',
                    }}
                  >
                    {bet.modelWin !== null ? `${(bet.modelWin * 100).toFixed(0)}%` : '-'}
                  </Typography>
                )}
              </Paper>
            </Tooltip>
          </div>
        </ClickAwayListener>
      </Stack>

      {/* Row 4: Buttons */}
      <Stack
        direction="row"
        gap="8px"
        sx={{ width: '100%' }}
      >
        <Button
          variant="outlined"
          disabled={isLocked}
          startIcon={isLocked ? <LockIcon size={16} /> : (isInBetSlip ? <RemoveIcon size={16} /> : <AddIcon size={16} />)}
          onClick={handleBetSlipClick}
          sx={{
            flex: 1,
            height: 38.85,
            borderRadius: '5.55px',
            border: isLocked ? '1px solid rgba(255, 255, 255, 0.2)' : (isInBetSlip ? '1px solid #EF5350' : '1px solid #42A5F5'),
            backgroundColor: 'transparent',
            fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
            fontWeight: 600,
            fontSize: '11.11px',
            lineHeight: 1.5,
            color: isLocked ? 'rgba(255, 255, 255, 0.3)' : (isInBetSlip ? '#EF5350' : '#42A5F5'),
            textTransform: 'none',
            '&:hover': {
              backgroundColor: isInBetSlip ? 'rgba(239, 83, 80, 0.08)' : 'rgba(66, 165, 245, 0.08)',
              border: isInBetSlip ? '1px solid #EF5350' : '1px solid #42A5F5',
            },
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          {isLocked ? 'Locked' : (isInBetSlip ? 'Remove from Bet Slip' : 'Add to Bet Slip')}
        </Button>
        <Button
          variant="contained"
          disabled={isLocked}
          onClick={() => {
            if (bet.deepLinkUrl) {
              const isMobile = isActualMobileDevice();
              const betSlipItem: BetSlipItem = {
                id: `${bet.playerId}-${bet.stat}-${bet.book}-${bet.side}`,
                playerName: bet.playerName,
                avatar: bet.avatar,
                stat: bet.stat,
                projection: bet.projection.toFixed(1),
                bookLine: bet.bookLine.toFixed(1),
                edge: bet.edge.toFixed(1),
                bookWin: bet.bookWin,
                modelWin: bet.modelWin,
                evPct: bet.evPct,
                book: bet.book,
                bookLogo: bet.bookIcon,
                odds: bet.odds,
                side: bet.side,
                deepLinkUrl: bet.deepLinkUrl,
              };
              const url = getPlatformDeepLink([betSlipItem], isMobile);
              if (url) {
                window.open(url, '_blank');
              }
            }
          }}
          sx={{
            flex: 1,
            height: 38.85,
            borderRadius: '5.55px',
            backgroundColor: isLocked ? 'rgba(255, 255, 255, 0.12)' : '#42A5F5',
            border: '1px solid rgba(42, 83, 195, 0.3)',
            fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
            fontWeight: 600,
            fontSize: '11.11px',
            lineHeight: 1.5,
            color: isLocked ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.87)',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#1976d2',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          Bet
        </Button>
      </Stack>

      {/* Expand Caret */}
      {onToggleExpand && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <CaretDown
            size={16}
            weight="bold"
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        </Box>
      )}

      {/* Expanded Odds Panel — shows ALL sportsbooks regardless of filter */}
      {isExpanded && (
        allBooksLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <CircularProgress size={20} />
          </Box>
        ) : allSportsbooks.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
              pt: 0.5,
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              overflow: 'hidden',
              '& > *': { minWidth: 0 },
            }}
          >
            {allSportsbooks.map((sb) => (
              <SportsbookCard key={sb.book} sb={sb} />
            ))}
          </Box>
        ) : null
      )}
    </Box>
  );
};

export default MobileBetCard;
